import { api, Vehicle, Shipment, Alert, exportVehiclesCSV } from '../api';
import { eventTypeLabel, eventTypeIcon } from '../main';
import { statusBadge, severityBadge, timeAgo, createFormModal, showModalError, formatKM, formatHours } from '@demo/ui';

function vehicleFormModal(onSubmit: (data: Record<string, string>) => void, onClose: () => void): HTMLElement {
  return createFormModal({
    title: 'New Vehicle',
    fields: [
      [
        { name: 'id', label: 'Vehicle ID', type: 'text', required: true, placeholder: 'e.g. TR-09' },
        { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'e.g. IceRunner' },
      ],
      [
        { name: 'type', label: 'Type', type: 'select', required: true, options: [{ value: 'reefer_truck', label: 'Reefer Truck' }, { value: 'sea_container', label: 'Sea Container' }] },
        { name: 'status', label: 'Status', type: 'select', options: [{ value: 'idle', label: 'Idle' }, { value: 'active', label: 'Active' }, { value: 'maintenance', label: 'Maintenance' }] },
      ],
      [
        { name: 'home_base', label: 'Home Base', type: 'text', required: true, placeholder: 'e.g. Istanbul' },
        { name: 'current_driver', label: 'Driver', type: 'text', placeholder: 'e.g. Mehmet Yilmaz' },
      ],
      [
        { name: 'latitude', label: 'Latitude', type: 'number', placeholder: '41.01', step: 'any' },
        { name: 'longitude', label: 'Longitude', type: 'number', placeholder: '28.98', step: 'any' },
      ],
    ],
    submitLabel: 'Create Vehicle',
    onSubmit,
    onClose,
  });
}

function renderVehicleGrid(container: HTMLElement, vehicles: Vehicle[]) {
  const grid = container.querySelector('#vehicle-grid')!;
  grid.innerHTML = vehicles.map(v => `
    <a href="#/vehicles/${v.id}" class="block bg-th-card rounded-2xl border border-th-border p-5 hover:bg-th-card-hover hover:border-th-border-2 transition-all duration-300 group card-glow">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl ${v.status === 'active' ? 'bg-green-500/10 ring-1 ring-green-500/20' : v.status === 'idle' ? 'bg-amber-500/10 ring-1 ring-amber-500/20' : 'bg-red-500/10 ring-1 ring-red-500/20'} flex items-center justify-center">
            ${v.type === 'reefer_truck' ? truckSvg(v.status) : containerSvg(v.status)}
          </div>
          <div>
            <p class="font-semibold group-hover:text-blue-400 transition-colors">${v.name}</p>
            <p class="text-xs text-th-text-4 font-mono">${v.id}</p>
          </div>
        </div>
        ${statusBadge(v.status)}
      </div>
      <div class="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider">Driver</p>
          <p class="text-th-text-2 mt-0.5">${v.current_driver || '—'}</p>
        </div>
        <div>
          <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider">Base</p>
          <p class="text-th-text-2 mt-0.5">${v.home_base}</p>
        </div>
        <div>
          <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider">Total KM</p>
          <p class="text-th-text-2 mt-0.5">${formatKM(v.total_km)}</p>
        </div>
        <div>
          <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider">Speed</p>
          <p class="text-th-text-2 mt-0.5">${v.speed > 0 ? v.speed.toFixed(0) + ' km/h' : '—'}</p>
        </div>
      </div>
    </a>
  `).join('');
}

export async function renderVehicles(container: HTMLElement) {
  let vehicles = await api.getVehicles();

  container.innerHTML = `
    <div class="h-full overflow-y-auto page-enter">
      <div class="p-4 sm:p-8">
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 class="text-xl sm:text-2xl font-bold tracking-tight">Vehicles</h1>
            <p class="text-sm text-th-text-3 mt-1" id="vehicle-count">${vehicles.length} registered vehicles</p>
          </div>
          <div class="flex items-center gap-2 sm:gap-3">
            <button id="export-vehicles-btn" class="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-th-surface hover:bg-th-surface-hover border border-th-border-2 text-th-text-2 text-sm font-medium rounded-xl transition-all hover:border-th-border-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
              <span class="hidden sm:inline">Export CSV</span><span class="sm:hidden">CSV</span>
            </button>
            <button id="new-vehicle-btn" class="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white text-sm font-medium rounded-xl transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
              New
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4" id="vehicle-grid"></div>
      </div>
    </div>
  `;

  renderVehicleGrid(container, vehicles);

  container.querySelector('#export-vehicles-btn')!.addEventListener('click', () => exportVehiclesCSV());

  container.querySelector('#new-vehicle-btn')!.addEventListener('click', () => {
    const modal = vehicleFormModal(
      async (data) => {
        try {
          const body: Record<string, unknown> = {
            id: data.id,
            name: data.name,
            type: data.type,
            home_base: data.home_base,
            current_driver: data.current_driver || '',
            status: data.status || 'idle',
            latitude: data.latitude ? parseFloat(data.latitude) : 0,
            longitude: data.longitude ? parseFloat(data.longitude) : 0,
          };
          await api.createVehicle(body);
          modal.remove();
          vehicles = await api.getVehicles();
          container.querySelector('#vehicle-count')!.textContent = `${vehicles.length} registered vehicles`;
          renderVehicleGrid(container, vehicles);
        } catch (err: any) {
          showModalError(modal, err.message);
        }
      },
      () => modal.remove(),
    );
    document.body.appendChild(modal);
  });
}

export async function renderVehicleDetail(container: HTMLElement, id: string) {
  const [vehicle, shipments, alerts] = await Promise.all([
    api.getVehicle(id),
    api.getVehicleShipments(id),
    api.getVehicleAlerts(id),
  ]);

  container.innerHTML = `
    <div class="h-full overflow-y-auto page-enter">
      <div class="p-4 sm:p-6 max-w-6xl">
        <div class="flex items-center gap-3 mb-6 flex-wrap">
          <a href="#/vehicles" class="text-th-text-3 hover:text-th-text transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
          </a>
          <div class="w-10 h-10 rounded-lg ${vehicle.status === 'active' ? 'bg-green-500/15' : vehicle.status === 'idle' ? 'bg-amber-500/15' : 'bg-red-500/15'} flex items-center justify-center">
            ${vehicle.type === 'reefer_truck' ? truckSvg(vehicle.status) : containerSvg(vehicle.status)}
          </div>
          <div class="min-w-0">
            <h1 class="text-xl sm:text-2xl font-bold truncate">${vehicle.name}</h1>
            <p class="text-xs sm:text-sm text-th-text-3">${vehicle.id} · ${vehicle.type === 'reefer_truck' ? 'Reefer Truck' : 'Sea Container'}</p>
          </div>
          <div class="ml-2">${statusBadge(vehicle.status)}</div>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
          <div class="bg-th-card rounded-2xl border border-th-border p-4 stat-card">
            <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider mb-1.5">Total Distance</p>
            <p class="text-xl font-bold">${formatKM(vehicle.total_km)}</p>
          </div>
          <div class="bg-th-card rounded-2xl border border-th-border p-4 stat-card">
            <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider mb-1.5">Uptime</p>
            <p class="text-xl font-bold">${formatHours(vehicle.uptime_hours)}</p>
          </div>
          <div class="bg-th-card rounded-2xl border border-th-border p-4 stat-card">
            <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider mb-1.5">Current Speed</p>
            <p class="text-xl font-bold text-blue-400">${vehicle.speed > 0 ? vehicle.speed.toFixed(0) + ' km/h' : 'Stopped'}</p>
          </div>
          <div class="bg-th-card rounded-2xl border border-th-border p-4 stat-card">
            <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider mb-1.5">Driver</p>
            <p class="text-base font-semibold">${vehicle.current_driver || 'Unassigned'}</p>
          </div>
          <div class="bg-th-card rounded-2xl border border-th-border p-4 stat-card">
            <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider mb-1.5">Home Base</p>
            <p class="text-base font-semibold">${vehicle.home_base}</p>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-th-surface rounded-xl border border-th-border-2 p-4 sm:p-5">
            <h3 class="text-sm font-semibold text-th-text-3 mb-4 uppercase tracking-wider">Shipment History</h3>
            ${shipments.length === 0 ? '<p class="text-th-text-3 text-sm">No shipments</p>' : `
              <div class="space-y-3">
                ${shipments.map(s => `
                  <a href="#/shipments/${s.id}" class="block p-3 rounded-lg bg-th-surface hover:bg-th-surface-hover transition-colors">
                    <div class="flex items-center justify-between mb-1">
                      <span class="text-sm font-medium text-blue-400">${s.id}</span>
                      ${statusBadge(s.status)}
                    </div>
                    <p class="text-sm text-th-text-2">${s.origin} → ${s.destination}</p>
                    <p class="text-xs text-th-text-3">${s.cargo_type} · ${s.cargo_description}</p>
                  </a>
                `).join('')}
              </div>
            `}
          </div>

          <div class="bg-th-surface rounded-xl border border-th-border-2 p-4 sm:p-5">
            <h3 class="text-sm font-semibold text-th-text-3 mb-4 uppercase tracking-wider">Recent Alerts</h3>
            ${alerts.length === 0 ? '<p class="text-th-text-3 text-sm">No alerts</p>' : `
              <div class="space-y-3">
                ${alerts.map(a => `
                  <div class="flex items-start gap-3 p-3 rounded-lg bg-th-surface ${a.resolved ? 'opacity-60' : ''}">
                    <div class="shrink-0 mt-0.5">${eventTypeIcon(a.event_type)}</div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="text-sm font-medium">${eventTypeLabel(a.event_type)}</span>
                        ${severityBadge(a.severity)}
                      </div>
                      <p class="text-xs text-th-text-3">${a.description}</p>
                      <p class="text-xs text-th-text-4 mt-1">${timeAgo(a.created_at)}</p>
                    </div>
                  </div>
                `).join('')}
              </div>
            `}
          </div>
        </div>
      </div>
    </div>
  `;
}

function truckSvg(status: string): string {
  const color = status === 'active' ? 'text-green-400' : status === 'idle' ? 'text-amber-400' : 'text-red-400';
  return `<svg class="w-5 h-5 ${color}" fill="currentColor" viewBox="0 0 20 20"><path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/><path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-1h3.05a2.5 2.5 0 014.9 0H19a1 1 0 001-1v-5a1 1 0 00-.293-.707l-3-3A1 1 0 0016 4H3zm13 2.5V9h3l-2.5-2.5H16z"/></svg>`;
}

function containerSvg(status: string): string {
  const color = status === 'active' ? 'text-green-400' : status === 'idle' ? 'text-amber-400' : 'text-red-400';
  return `<svg class="w-5 h-5 ${color}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>`;
}
